import { booleanAttribute } from "#ce/utils/props";
import { isValidSkeletonTypeInput, skeletonAnimationOptions } from "./types";

// We had bugs in the past, where the props were mutated. Apply Object.freeze() to prevent this.
export const props = Object.freeze({
  /**
   * Skeleton shape DSL. Accepts a single primitive (e.g. "image"), a repeated primitive via `@N` (e.g. "text@3"), or a comma-separated composition (e.g. "heading, paragraph@2, button").
   *
   * Supported types: text, heading, paragraph, image, avatar, chip, button, divider, doctor-finder-result.
   *
   * @type {String}
   * @default "image"
   * @storybook-control text
   */
  type: {
    type: String,
    default: "image",
    validator: isValidSkeletonTypeInput,
  },

  /**
   * Show the skeleton placeholders. Set to `false` to swap the placeholders for the default slot content.
   *
   * @type {Boolean|String}
   * @default true
   */
  loading: booleanAttribute(true),

  /**
   * Accessible loading announcement text used by screen readers.
   *
   * @type {String}
   * @default "Loading"
   */
  loadingText: {
    type: String,
    default: "Loading",
  },

  /**
   * Animation style for the skeleton. "wave" sweeps a gradient across each bone; "pulse" opacity-pulses each bone in place.
   *
   * @type {String}
   * @default "wave"
   * @values wave, pulse
   * @storybook-control radio
   */
  animation: {
    type: String,
    default: "wave",
    validator: (value) => skeletonAnimationOptions.includes(value),
  },

  /**
   * Disable the animation and the accessibility loading announcement for decorative (non-loading) placeholders.
   *
   * @type {Boolean|String}
   * @default false
   */
  boilerplate: booleanAttribute(),

  /**
   * Elevation tier applied to the skeleton container (Sparkle elevation tiers 1, 2, or 3).
   *
   * @type {Number}
   * @default undefined
   * @values 1, 2, 3
   * @storybook-control radio
   */
  elevation: {
    type: Number,
    default: undefined,
    validator: (value) => [1, 2, 3].includes(value),
  },

  /**
   * Width of the skeleton container. Accepts standard CSS size values (px, rem, %, etc.). Unitless numbers are converted to rem.
   *
   * @type {Number|String}
   * @default undefined
   */
  width: {
    type: [Number, String],
    default: undefined,
  },

  /**
   * Height of the skeleton container. Accepts standard CSS size values (px, rem, %, etc.). Unitless numbers are converted to rem.
   *
   * @type {Number|String}
   * @default undefined
   */
  height: {
    type: [Number, String],
    default: undefined,
  },

  /**
   * Minimum width of the skeleton container. Accepts standard CSS size values (px, rem, %, etc.). Unitless numbers are converted to rem.
   *
   * @type {Number|String}
   * @default undefined
   */
  minWidth: {
    type: [Number, String],
    default: undefined,
  },

  /**
   * Minimum height of the skeleton container. Accepts standard CSS size values (px, rem, %, etc.). Unitless numbers are converted to rem.
   *
   * @type {Number|String}
   * @default undefined
   */
  minHeight: {
    type: [Number, String],
    default: undefined,
  },

  /**
   * Maximum width of the skeleton container. Accepts standard CSS size values (px, rem, %, etc.). Unitless numbers are converted to rem.
   *
   * @type {Number|String}
   * @default undefined
   */
  maxWidth: {
    type: [Number, String],
    default: undefined,
  },

  /**
   * Maximum height of the skeleton container. Accepts standard CSS size values (px, rem, %, etc.). Unitless numbers are converted to rem.
   *
   * @type {Number|String}
   * @default undefined
   */
  maxHeight: {
    type: [Number, String],
    default: undefined,
  },
});
